import { HttpClient } from '@/data/protocols/http'
import { AssessmentProfile } from '@/domain/models/profile-assessment/assessment-profile'
import { AssessmentCompetence } from '@/domain/models/profile-assessment/assessment-competence'
import { UserProfileAssessment } from '@/domain/usecases/profile-assessment/user-profile-assessment'
import { GenericResponse } from '@/infra/http/generic-response'
import { QueryClient } from 'react-query'
import { BaseRemote } from '@/data/usecases/base-remote'

export type UserProfileCompetenceResponse = {
  competence: string
  marketScore: number
  companyScore: number
  userScore: number
  marketDistance: number
}

export type UserProfileResponse = {
  url: string
  finished: boolean
  finishedAt: string | null
  expiresAt: string
  externalUniqueId: string
  profile: string | null
  competences: UserProfileCompetenceResponse[] | null
}

export class RemoteUserProfileAssessment extends BaseRemote implements UserProfileAssessment {
  constructor(
    private readonly queryClient: QueryClient,
    private readonly url: string,
    private readonly httpClient: HttpClient<GenericResponse<UserProfileAssessment.Model>>
  ) {
    super()
  }

  async getLatest(): Promise<UserProfileAssessment.Model | null> {
    const response = await this.queryClient.fetchQuery({
      queryKey: UserProfileAssessment.KeyGet,
      queryFn: async () => {
        return this.httpClient.request({
          url: `${this.url}/latest`,
          method: 'get'
        })
      }
    })
    const { data } = this.onSuccess(response) as GenericResponse<UserProfileResponse>
    if (!data) {
      return null
    }
    return {
      ...data,
      profile: AssessmentProfile.getByName(data.profile),
      competences:
        data.competences?.map(c => ({
          ...c,
          competence: AssessmentCompetence.getByName(c.competence)
        })) || null
    }
  }

  async create(): Promise<UserProfileAssessment.Model | null> {
    const response = await this.queryClient.fetchQuery({
      queryKey: UserProfileAssessment.KeyGet,
      queryFn: async () => {
        return this.httpClient.request({
          url: this.url,
          method: 'post'
        })
      }
    })
    const { data } = this.onSuccess(response) as GenericResponse<UserProfileAssessment.Model>
    return data
  }

  async sync(externalUniqueId: string): Promise<UserProfileAssessment.Model | null> {
    const response = await this.queryClient.fetchQuery({
      queryKey: UserProfileAssessment.KeyGet,
      queryFn: async () => {
        return this.httpClient.request({
          url: `${this.url}/${externalUniqueId}/sync`,
          method: 'post'
        })
      }
    })
    const { data } = this.onSuccess(response) as GenericResponse<UserProfileAssessment.Model>
    return data
  }
}
